import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface ServiceAddon {
  id: string;
  name: string;
  description: string;
  price: number;
  duration?: string;
  popular?: boolean;
}

export interface FirestoreService {
  id: string;
  name: string;
  description: string;
  longDescription?: string;
  category: string;
  categoryId?: string;
  price: number;
  originalPrice?: number;
  duration: string;
  rating: number;
  reviewCount?: number;
  reviews?: number;
  image: string;
  images?: string[];
  features?: string[];
  includes?: string[];
  provider?: string;
  providerDetails?: {
    name: string;
    experience: string;
    rating: number;
    reviewCount: number;
    location: string;
    verified: boolean;
  };
  location?: string;
  discount?: number;
  
  // Tags
  popular?: boolean;
  featured?: boolean;
  new?: boolean;
  trending?: boolean;
  
  // Display options
  showOnHomePage?: boolean;
  showInPopularSection?: boolean;
  showInFeaturedSection?: boolean;
  
  // Addons
  hasAddons?: boolean;
  addons?: ServiceAddon[];
  
  // SEO & Keywords
  keywords?: string[];
  metaTitle?: string;
  metaDescription?: string;
  
  // Status
  status?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface FirestoreCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color?: string;
  backgroundColor?: string;
  image: string;
  serviceCount?: number;
  popular?: boolean;
  showOnHomepage?: boolean;
  order?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// In-memory cache for services (5 minutes)
const servicesCache: { data: FirestoreService[] | null; timestamp: number | null } = {
  data: null,
  timestamp: null
};

// In-memory cache for categories (5 minutes)
const categoriesCache: { data: FirestoreCategory[] | null; timestamp: number | null } = {
  data: null,
  timestamp: null
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Hook to fetch all services (with caching)
export const useServices = () => {
  const [services, setServices] = useState<FirestoreService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        
        // Check cache first
        const now = Date.now();
        if (servicesCache.data && servicesCache.timestamp && (now - servicesCache.timestamp < CACHE_DURATION)) {
          setServices(servicesCache.data);
          setLoading(false);
          return;
        }
        
        const servicesRef = collection(db, 'services');
        // Try ordered query first
        try {
          const q = query(servicesRef, orderBy('createdAt', 'desc'));
          const snapshot = await getDocs(q);
          const servicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as FirestoreService[];
          
          // Update cache
          servicesCache.data = servicesData;
          servicesCache.timestamp = Date.now();
          
          setServices(servicesData);
        } catch (orderedErr) {
          const snapshot = await getDocs(servicesRef);
          const servicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as FirestoreService[];
          
          // Update cache
          servicesCache.data = servicesData;
          servicesCache.timestamp = Date.now();
          
          setServices(servicesData);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Failed to load services');
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return { services, loading, error, refetch: () => {} };
};

// Hook to fetch services by category
export const useServicesByCategory = (categoryId: string) => {
  const [services, setServices] = useState<FirestoreService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const servicesRef = collection(db, 'services');
        const q = query(
          servicesRef,
          where('categoryId', '==', categoryId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        
        const servicesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as FirestoreService[];
        
        setServices(servicesData);
        setError(null);
      } catch (err) {
        console.error('Error fetching services by category:', err);
        setError('Failed to load services');
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchServices();
    }
  }, [categoryId]);

  return { services, loading, error };
};

// Hook to fetch a single service by ID
export const useService = (serviceId: string | undefined) => {
  const [service, setService] = useState<FirestoreService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchService = async () => {
      if (!serviceId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const serviceRef = doc(db, 'services', serviceId);
        const snapshot = await getDoc(serviceRef);
        
        if (snapshot.exists()) {
          setService({
            id: snapshot.id,
            ...snapshot.data()
          } as FirestoreService);
          setError(null);
        } else {
          setError('Service not found');
          setService(null);
        }
      } catch (err) {
        console.error('Error fetching service:', err);
        setError('Failed to load service');
        setService(null);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceId]);

  return { service, loading, error };
};

// Hook to fetch all categories (with caching)
export const useCategories = () => {
  const [categories, setCategories] = useState<FirestoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        
        // Check cache first
        const now = Date.now();
        if (categoriesCache.data && categoriesCache.timestamp && (now - categoriesCache.timestamp < CACHE_DURATION)) {
          setCategories(categoriesCache.data);
          setLoading(false);
          return;
        }
        
        const categoriesRef = collection(db, 'categories');
        const snapshot = await getDocs(categoriesRef);
        
        const categoriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as FirestoreCategory[];
        
        // Sort in memory by order field
        categoriesData.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          return a.name.localeCompare(b.name);
        });
        
        // Update cache
        categoriesCache.data = categoriesData;
        categoriesCache.timestamp = Date.now();
        
        setCategories(categoriesData);
        setError(null);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
};

// Hook to fetch a single category by ID
export const useCategory = (categoryId: string | undefined) => {
  const [category, setCategory] = useState<FirestoreCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategory = async () => {
      if (!categoryId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const categoryRef = doc(db, 'categories', categoryId);
        const snapshot = await getDoc(categoryRef);
        
        if (snapshot.exists()) {
          setCategory({
            id: snapshot.id,
            ...snapshot.data()
          } as FirestoreCategory);
          setError(null);
        } else {
          setError('Category not found');
          setCategory(null);
        }
      } catch (err) {
        console.error('Error fetching category:', err);
        setError('Failed to load category');
        setCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [categoryId]);

  return { category, loading, error };
};

// Interface for Service Packages
export interface ServicePackage {
  id: string;
  name: string;
  description: string;
  serviceIds: string[];
  price: number;
  originalPrice?: number;
  discount?: number;
  savings: number;
  image: string;
  images?: string[];
  
  // Tags
  popular?: boolean;
  featured?: boolean;
  new?: boolean;
  trending?: boolean;
  
  // Display options
  showInPopularSection?: boolean;
  
  // Status
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Hook to fetch all packages
export const usePackages = () => {
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const packagesRef = collection(db, 'packages');
        const q = query(packagesRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        
        const packagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ServicePackage));

        setPackages(packagesData);
        setError(null);
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load packages');
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  return { packages, loading, error };
};
